/**
 * The cinematic backdrop: a slowly turning globe of connected people drawn on one fixed canvas behind the app.
 * Gold arcs light up between two points now and then: a "match", which is what VConnect does.
 *
 * Lives outside #app so re-renders never touch it. Plain 2D canvas, no libraries. It rests when the tab is
 * hidden, draws a single still frame for people who ask for reduced motion, and dims itself inside the app
 * shell so dashboards stay calm.
 */
import { isDark } from './theme.js';

const TAU = Math.PI * 2;
const POINTS = 420;
const LINK = 0.24; // max 3D distance (unit sphere) for a hairline between two points

let canvas;
let ctx;
let width = 0;
let height = 0;
let dpr = 1;
let points = [];
let links = [];
let arcs = [];
let frame = 0;
let running = false;
let last = 0;
let angle = 0;
let still = false;

function palette() {
  return isDark()
    ? { dot: [160, 158, 247], line: [160, 158, 247], gold: [226, 181, 88], core: [51, 48, 168], dotA: 0.95, lineA: 0.34, glowA: 0.55 }
    : { dot: [51, 48, 168], line: [51, 48, 168], gold: [184, 134, 34], core: [160, 158, 247], dotA: 0.6, lineA: 0.2, glowA: 0.4 };
}

/** Even points on a sphere (Fibonacci lattice), then hairlines between near neighbours. */
function build() {
  points = [];
  const golden = Math.PI * (3 - Math.sqrt(5));
  for (let i = 0; i < POINTS; i += 1) {
    const y = 1 - (i / (POINTS - 1)) * 2;
    const r = Math.sqrt(1 - y * y);
    const t = golden * i;
    points.push({ x: Math.cos(t) * r, y, z: Math.sin(t) * r, s: 0.6 + ((i * 7919) % 100) / 100 });
  }
  links = [];
  for (let i = 0; i < POINTS; i += 1) {
    let made = 0;
    for (let j = i + 1; j < POINTS && made < 4; j += 1) {
      const a = points[i];
      const b = points[j];
      const d = Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
      if (d < LINK) {
        links.push([i, j]);
        made += 1;
      }
    }
  }
}

function resize() {
  dpr = Math.min(window.devicePixelRatio || 1, 1.5);
  width = window.innerWidth;
  height = window.innerHeight;
  canvas.width = Math.round(width * dpr);
  canvas.height = Math.round(height * dpr);
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  if (still) draw(0);
}

/** Where the globe sits: right of the hero on wide screens, drifting to centre as the page scrolls. */
function stage() {
  const inApp = document.body.classList.contains('in-app');
  const wide = width > 900;
  const scroll = Math.min(window.scrollY / Math.max(height, 1), 1.4);
  const base = Math.min(width, height);
  if (inApp) return { cx: width - base * 0.18, cy: height * 0.82, r: base * 0.42, alpha: 0.32 };
  const startX = wide ? width * 0.72 : width * 0.5;
  const cx = startX + (width * 0.5 - startX) * Math.min(scroll, 1);
  const cy = (wide ? height * 0.5 : height * 0.62) + scroll * height * 0.02;
  const r = (wide ? base * 0.36 : base * 0.46) * (1 + scroll * 0.18);
  return { cx, cy, r, alpha: wide ? 1 : 0.7 };
}

function rotate(p, ay, ax) {
  const cy = Math.cos(ay);
  const sy = Math.sin(ay);
  const x = p.x * cy - p.z * sy;
  let z = p.x * sy + p.z * cy;
  const cx = Math.cos(ax);
  const sx = Math.sin(ax);
  const y = p.y * cx - z * sx;
  z = p.y * sx + z * cx;
  return { x, y, z };
}

function rgba([r, g, b], a) {
  return `rgba(${r},${g},${b},${a})`;
}

function maybeArc(now) {
  if (arcs.length >= 3 || Math.random() > 0.012) return;
  const a = Math.floor(Math.random() * POINTS);
  let b = Math.floor(Math.random() * POINTS);
  if (b === a) b = (b + 37) % POINTS;
  arcs.push({ a, b, born: now, life: 2600 });
}

function draw(now) {
  const col = palette();
  const { cx, cy, r, alpha } = stage();
  ctx.clearRect(0, 0, width, height);
  ctx.globalAlpha = alpha;

  // core glow
  const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, r * 1.25);
  glow.addColorStop(0, rgba(col.core, col.glowA));
  glow.addColorStop(0.45, rgba(col.core, col.glowA * 0.25));
  glow.addColorStop(1, rgba(col.core, 0));
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(cx, cy, r * 1.25, 0, TAU);
  ctx.fill();

  const tilt = 0.38 + Math.sin(angle * 0.3) * 0.05;
  const projected = points.map((p) => {
    const q = rotate(p, angle, tilt);
    const depth = (q.z + 1.6) / 2.6; // 0.23..1
    return { x: cx + q.x * r, y: cy + q.y * r, z: q.z, d: depth };
  });

  // hairlines, back to front
  ctx.lineWidth = 0.6;
  for (const [i, j] of links) {
    const a = projected[i];
    const b = projected[j];
    const depth = (a.d + b.d) / 2;
    ctx.strokeStyle = rgba(col.line, col.lineA * depth * depth);
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
  }

  // people
  for (let i = 0; i < projected.length; i += 1) {
    const p = projected[i];
    const size = (0.5 + p.d * 1.3) * points[i].s;
    ctx.fillStyle = rgba(col.dot, col.dotA * p.d * p.d);
    ctx.beginPath();
    ctx.arc(p.x, p.y, size, 0, TAU);
    ctx.fill();
  }

  // matches: a gold arc lifts off the surface between two people, then fades
  arcs = arcs.filter((arc) => now - arc.born < arc.life);
  for (const arc of arcs) {
    const t = (now - arc.born) / arc.life;
    const a = projected[arc.a];
    const b = projected[arc.b];
    if (a.z < -0.2 && b.z < -0.2) continue;
    const fade = Math.sin(Math.PI * t);
    const mx = (a.x + b.x) / 2;
    const my = (a.y + b.y) / 2;
    const lift = Math.hypot(a.x - b.x, a.y - b.y) * 0.45;
    const nx = mx + ((mx - cx) / r) * lift;
    const ny = my + ((my - cy) / r) * lift;
    const grow = Math.min(t * 2.2, 1);
    ctx.strokeStyle = rgba(col.gold, 0.85 * fade);
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    for (let k = 1; k <= 24 * grow; k += 1) {
      const s = k / 24;
      const x = (1 - s) * (1 - s) * a.x + 2 * (1 - s) * s * nx + s * s * b.x;
      const y = (1 - s) * (1 - s) * a.y + 2 * (1 - s) * s * ny + s * s * b.y;
      ctx.lineTo(x, y);
    }
    ctx.stroke();
    for (const end of [a, b]) {
      ctx.fillStyle = rgba(col.gold, fade);
      ctx.beginPath();
      ctx.arc(end.x, end.y, 2.4, 0, TAU);
      ctx.fill();
      ctx.fillStyle = rgba(col.gold, 0.18 * fade);
      ctx.beginPath();
      ctx.arc(end.x, end.y, 9 * fade, 0, TAU);
      ctx.fill();
    }
  }
  ctx.globalAlpha = 1;
}

function tick(now) {
  if (!running) return;
  const dt = Math.min(now - (last || now), 64);
  last = now;
  angle += dt * 0.00009;
  maybeArc(now);
  draw(now);
  frame = requestAnimationFrame(tick);
}

function play() {
  if (running || still || document.hidden) return;
  running = true;
  last = 0;
  frame = requestAnimationFrame(tick);
}

function pause() {
  running = false;
  cancelAnimationFrame(frame);
}

export function installScene() {
  if (canvas || typeof window === 'undefined' || !document.body) return;
  canvas = document.createElement('canvas');
  canvas.className = 'scene';
  canvas.setAttribute('aria-hidden', 'true');
  ctx = canvas.getContext('2d');
  if (!ctx) return;
  document.body.prepend(canvas);
  const motion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
  still = Boolean(motion?.matches);
  build();
  resize();
  window.addEventListener('resize', resize, { passive: true });
  window.addEventListener('scroll', () => { if (still) draw(0); }, { passive: true });
  document.addEventListener('visibilitychange', () => (document.hidden ? pause() : play()));
  motion?.addEventListener?.('change', (e) => {
    still = e.matches;
    if (still) { pause(); draw(0); } else play();
  });
  if (still) draw(0);
  else play();
}

/** Redraw after the theme or the page type changes (needed when animation is off). */
export function refreshScene() {
  if (ctx && still) draw(0);
}
